/**
 * Avaliador de expressões aritméticas/lógicas seguro (sem eval).
 * ESPELHO de takaokabots-api/app/commons/forms/expression.js.
 * Mantenha a semântica idêntica nos dois lados.
 */

function tokenize(input) {
  const tokens = [];
  const re = /\s*([A-Za-z_][A-Za-z0-9_]*|\d+\.?\d*|\.\d+|>=|<=|==|!=|&&|\|\||[-+*/%(),<>!])\s*/g;
  let match;
  let lastIndex = 0;
  while ((match = re.exec(input)) !== null) {
    if (match.index !== lastIndex) throw new Error(`Caractere inesperado em "${input}"`);
    tokens.push(match[1]);
    lastIndex = re.lastIndex;
  }
  if (lastIndex !== input.length) throw new Error(`Expressão inválida: "${input}"`);
  return tokens;
}

const FUNCTIONS = {
  min: (args) => Math.min(...args),
  max: (args) => Math.max(...args),
  abs: (args) => Math.abs(args[0]),
  round: (args) => Math.round(args[0]),
  floor: (args) => Math.floor(args[0]),
  ceil: (args) => Math.ceil(args[0]),
  sqrt: (args) => Math.sqrt(args[0]),
  if: (args) => (toBool(args[0]) ? args[1] : args[2]),
  sum: (args) => args.reduce((a, b) => a + b, 0),
  avg: (args) => (args.length ? args.reduce((a, b) => a + b, 0) / args.length : 0),
  count: (args) => args.filter((v) => v !== 0 && v !== null && v !== undefined).length,
};

export function toBool(v) {
  return !!v && v !== 0;
}

export function toNum(v) {
  if (v === true) return 1;
  if (v === false || v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

/** Idade em anos completos, a partir de uma data (string ISO "yyyy-mm-dd" ou Date). */
/**
 * "yyyy-mm-dd" é parseado como meia-noite UTC pelo `Date`. Comparada com a data local,
 * essa string antecipa o aniversário em fusos negativos — em UTC-3 o paciente cruzava
 * o corte de risco etário um dia antes. Aqui a data nasce local.
 */
function parseLocalDate(raw) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw).trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function computeAge(raw) {
  if (!raw) return 0;
  const birth = raw instanceof Date ? raw : parseLocalDate(raw);
  if (!birth || isNaN(birth.getTime())) return 0;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age < 0 ? 0 : age;
}

function parse(tokens, scope) {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expect = (t) => {
    if (tokens[pos] !== t) throw new Error(`Esperado "${t}"`);
    pos++;
  };

  function parsePrimary() {
    const tok = peek();
    if (tok === undefined) throw new Error("Fim inesperado da expressão");
    if (tok === "(") {
      next();
      const v = parseOr();
      expect(")");
      return v;
    }
    if (tok === "-") { next(); return -parsePrimary(); }
    if (tok === "!") { next(); return toBool(parsePrimary()) ? 0 : 1; }
    if (/^\d/.test(tok) || /^\.\d/.test(tok)) { next(); return parseFloat(tok); }
    if (/^[A-Za-z_]/.test(tok)) {
      next();
      if (peek() === "(") {
        next();
        // age(campo_data): usa o valor bruto (string de data) do campo referenciado,
        // sem passar pelo toNum (que zeraria uma data não-numérica).
        if (tok.toLowerCase() === "age") {
          const idTok = peek();
          let raw;
          if (idTok !== ")" && /^[A-Za-z_]/.test(idTok)) { next(); raw = scope[idTok]; }
          else raw = parseOr();
          expect(")");
          return computeAge(raw);
        }
        const args = [];
        if (peek() !== ")") {
          args.push(parseOr());
          while (peek() === ",") { next(); args.push(parseOr()); }
        }
        expect(")");
        const fn = FUNCTIONS[tok.toLowerCase()];
        if (!fn) throw new Error(`Função desconhecida: ${tok}`);
        return fn(args.map(toNum));
      }
      if (tok === "true") return 1;
      if (tok === "false") return 0;
      return toNum(scope[tok]);
    }
    throw new Error(`Token inesperado: ${tok}`);
  }

  function parseMul() {
    let left = parsePrimary();
    while (peek() === "*" || peek() === "/" || peek() === "%") {
      const op = next();
      const right = parsePrimary();
      if (op === "*") left = left * right;
      else if (op === "/") left = right === 0 ? 0 : left / right;
      else left = right === 0 ? 0 : left % right;
    }
    return left;
  }

  function parseAdd() {
    let left = parseMul();
    while (peek() === "+" || peek() === "-") {
      const op = next();
      const right = parseMul();
      left = op === "+" ? left + right : left - right;
    }
    return left;
  }

  function parseCmp() {
    let left = parseAdd();
    while ([">", ">=", "<", "<=", "==", "!="].includes(peek())) {
      const op = next();
      const right = parseAdd();
      switch (op) {
        case ">": left = left > right ? 1 : 0; break;
        case ">=": left = left >= right ? 1 : 0; break;
        case "<": left = left < right ? 1 : 0; break;
        case "<=": left = left <= right ? 1 : 0; break;
        case "==": left = left === right ? 1 : 0; break;
        case "!=": left = left !== right ? 1 : 0; break;
        default: break;
      }
    }
    return left;
  }

  function parseAnd() {
    let left = parseCmp();
    while (peek() === "&&" || peek() === "and") {
      next();
      const right = parseCmp();
      left = toBool(left) && toBool(right) ? 1 : 0;
    }
    return left;
  }

  function parseOr() {
    let left = parseAnd();
    while (peek() === "||" || peek() === "or") {
      next();
      const right = parseAnd();
      left = toBool(left) || toBool(right) ? 1 : 0;
    }
    return left;
  }

  const result = parseOr();
  if (pos !== tokens.length) throw new Error("Tokens sobrando na expressão");
  return result;
}

export function evaluateExpression(expr, scope) {
  try {
    return evaluateExpressionStrict(expr, scope);
  } catch (e) {
    return 0;
  }
}

/**
 * Mesma avaliação, mas propagando o erro de sintaxe. Serve ao builder: quem escreve a
 * fórmula precisa ver que ela está quebrada, em vez de receber o 0 que o runtime usa
 * para não derrubar o envio do paciente.
 */
export function evaluateExpressionStrict(expr, scope) {
  if (expr === null || expr === undefined || String(expr).trim() === "") return 0;
  return parse(tokenize(String(expr)), scope || {});
}
